import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs/Subscription';
import { Observable } from 'rxjs/Rx';

import { IApplicationState } from '../../stateManagement/states/applicationState';
import * as stageTypes from '../types';
import * as fromRoot from '../../stateManagement/rootReducer';
import * as stagesActions from '../../stateManagement/actions/data/stages';
import * as stageActions from '../../stateManagement/actions/data/stage';
import * as commonTypes from '../../shared/types';

import * as _ from 'lodash';
import { BUTTON_TYPES, STAGE_LIST } from '../types/constants';

import { StageService } from './stage.service';
import { FileUploader, FileItem, ParsedResponseHeaders } from 'ng2-file-upload';
import { EnableBusyFlag, DisableBusyFlag } from '../../stateManagement/actions/containers/application';

@Injectable()
export class StageSandbox {

    private subscriptions: Array<Subscription> = [];
    private currentStageId: number;
    private currentPageId: number;

    private hasStageInfo;
    private hasStagePages;
    private isAuth: boolean;
    private isFormDirty;

    constructor(
        private router: Router,
        private store: Store<IApplicationState>,
        private service: StageService
    ) {
        this.subscriptions.push(
            this.store.select(fromRoot.getStageDataCurrentStageId).subscribe( (stageId: number) => this.currentStageId = stageId),
            this.store.select(fromRoot.getStageDataGetSelectedPageId).subscribe ((pageId: number) => this.currentPageId = pageId),
            this.store.select(fromRoot.getAuthenticationIsAuthenticated).subscribe((isAuth: boolean) => this.isAuth = isAuth)
        );
    }

    getType() {
        return this.service.getType();
    }


    getTypeID() {
        return this.service.getTypeID();
    }

    onComponentDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    getToken$(): Observable<string> {
      //  return Observable.of('asdasdsad').distinct();
        return Observable.of(this.service.getToken()).distinct();
    }

    // for stage-container-component
    getCurrentStageId$(): Observable<number> {
        return this.store.select(fromRoot.getStageDataCurrentStageId);
    }

    isDataLoading$() {
        return this.store.select(fromRoot.getStageDataIsDataLoading);
    }

    getStageInfo_Data$() {
        // be called in ngInit to load current Stage Information
        this.subscriptions.push(
            this.store.select(fromRoot.getStageDataHasStageInfo)
                .subscribe( hasStageInfo => {
                    if (!hasStageInfo && this.isAuth) {
                        // console.log('Ooopss No Stage Info. need to call API to retrieve');
                        this.store.dispatch(new stagesActions.GetStageInfoAction({}));
                    }
                })
        );
    }

    getStageList() {
        return _.filter(STAGE_LIST, state => state.visible);
    }

    getCurrentStageInfo() {
        return _.find(STAGE_LIST, (stageInfo) => stageInfo.id === this.currentStageId);
    }

    acceptCurrentStage$(): Observable<any> {
        return this.service.acceptStage();
    }

    declineCurrentStage$(): Observable<any> {
        return this.service.declineStage();
    }

    // for page-container-component
    getCurrentPage$(): Observable<stageTypes.IPage> {
        return this.store.select(fromRoot.getStageDataGetSelectedPageData);
    }

    downloadAttachment(questionIndex: number): Subscription {
        return this.store.select(fromRoot.getStageDataGetSelectedPageData)
            .subscribe ( (pageData: stageTypes.IPage) => {
                if (pageData) {
                    const questionData = pageData.fields[questionIndex];
                    const payload: stageTypes.IAttachmentDownload = {
                        questionId: questionIndex,
                        name: questionData.header,
                        link: questionData.content,
                        action: 'download',
                        authRequired: true
                    };
                    this.dispatchDownload(payload);
                }
            });
    }

    simpleDownload(href: string, fid: number, label: string): any {

        const payload: stageTypes.IAttachmentDownload = {
            questionId: fid,
            name: label,
            link: href,
            action: 'download',
            authRequired: true
        };

        this.dispatchDownload(payload);

        return true;
    }

    dispatchDownload(payload) {
        this.store.dispatch(new stageActions.DownloadAttachmentAction(payload));
    }

    getFileReadingStatus$(questionIndex: number): Observable<boolean> {
        return this.store.select(fromRoot.getStageDataGetReadAttachments)
            .map( (statuses: commonTypes.IStatus[]) => {
                if (!statuses || (statuses && !statuses.length)) {
                    return false;
                }
                const readAttachment: commonTypes.IStatus = _.find(statuses, (status: commonTypes.IStatus) => status.currentId === String(questionIndex));
                return (readAttachment && readAttachment.downloaded);
            });
    }

    getFileContent$(questionIndex: number): Observable<any> {
        return this.store.select(fromRoot.getStageDataGetReadAttachments)
            .map( (statuses: commonTypes.IStatus[]) => {
                if (!statuses || (statuses && !statuses.length)) {
                    return '';
                }
                const readAttachment: commonTypes.IStatus = _.find(statuses, (status: commonTypes.IStatus) => status.currentId === String(questionIndex));
                return (readAttachment && readAttachment.result) ? readAttachment.result : '';
            });
    }

    getFileDownloadingStatus$(questionIndex: any, action: string): Observable<boolean> {
        return this.store.select(fromRoot.getStageDataGetAttachmentDownloadingStatuses)
            .map ( (statuses: commonTypes.IStatus[]) => {
                const questionDownloadingStatus: commonTypes.IStatus = _.find(statuses || [], (status: commonTypes.IStatus) => (status.currentId === String(questionIndex)) && (status.currentAction === action));
                if (questionDownloadingStatus) {
                    return questionDownloadingStatus.loading;
                }
                return false;
            });
    }

    // for welcome-container
    getAllAttachmentsDownloadingStatus$(): Observable<boolean> {
        return this.store.select(fromRoot.getStageDataAllAttachmentsDownloaded);
    }

    offerSelected$(type: string, data?: Object): Observable<any> {
        if (type.toUpperCase() === BUTTON_TYPES.ACCEPT) {
            return this.acceptCurrentStage$();
        } else if (type.toUpperCase() === BUTTON_TYPES.BACK || type.toUpperCase() === BUTTON_TYPES.SEND_MESSAGE) {
            return this.service.queryOffer(data || { message: '' });
        }
    }

    resetCurrentStageId(): void {
        this.store.dispatch(new stageActions.ResetStageAction(1));
    }

    resetCurrentStageById(id: number): void {
        this.store.dispatch(new stageActions.ResetStageAction(id));
    }

    navigatePage(nextPageId: number, pageData, isFormDirty: boolean, isFormValid: boolean, updatedFileInfo, updateFn, showingErrorFn): any {
        const data: stageTypes.IFormData = {
            currentPageId: this.currentPageId,
            pageData: _.cloneDeep(pageData),
            nextPageId: nextPageId
        };

        let errorUploads: any[] = [];

        const effect = (this.currentPageId > nextPageId) ? 'fadeInDown' : 'fadeInUp';
        const  isFormDirtyAndValid: boolean = this.isFormDirty  && isFormValid;

        // (nextPageId === this.currentPageId) ==> Save button or Submit button
        // (_.has(pageData, 'draft') && pageData.draft === 1 && isFormDirty) => in case previous button is clicked and form is dirty
        const alwaysSaveData: boolean = (nextPageId === this.currentPageId) ||
            (_.has(pageData, 'draft') && pageData.draft === 1 && this.isFormDirty );

        var numOfOldFiles = 0;
        // remove existing file that has fileid from queue, so the same file won't be uploaded again
        if (this.service.getUploaderService().queue.length > 0) {

          // find all instances
          let fileItem = _.filter(
            this.service.getUploaderService().queue,
            (fItem: FileItem) => fItem.additional['fileid'] !== undefined && fItem.additional['fileid'] != '');

            //console.log(fileItem);

          if (fileItem !== undefined && fileItem.length > 0) {
            //this.service.getUploaderService().removeFromQueue(fileItem);
            numOfOldFiles = fileItem.length;
          }

        }
        // remove existing file that has fileid from queue, so the same file won't be uploaded again
        //console.log('old files ' + numOfOldFiles);
        //console.log('queue size ' + this.service.getUploaderService().queue.length);
        //console.log('old queue ');
        //console.log(this.service.getUploaderService().queue);

        // remove files that has no file id and uploaded is true if navigate to other page, it will be gone on the UI page
        // for save current page, we do not remove already uploaded files from the queue, it stays on the UI page
        if (!(isFormDirtyAndValid || alwaysSaveData)) {
          for (var i = 0; i < this.service.getUploaderService().queue.length; i++) {
            // upload FileItem individually
            var fileItem = this.service.getUploaderService().queue[i];
            if (fileItem.additional && fileItem.additional['uploaded'] !== undefined && fileItem.additional['uploaded'] === true) {
              //console.log("remove already uploaded file from the queue, fileitem index " + fileItem);
              this.service.getUploaderService().removeFromQueue(fileItem);
            }

          }
        }

        //console.log('new queue ');
        //console.log(this.service.getUploaderService().queue);

        // when page naviage, go back or next, update file attachments if uploader queue has at least one new file
        if (this.service.getUploaderService().queue.length > 0 && this.service.getUploaderService().queue.length - numOfOldFiles > 0) {

            this.service.getUploaderService().onProgressItem = (item, progress) => {
                this.store.dispatch(new EnableBusyFlag(progress));
            };

            this.service.getUploaderService().onErrorItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
                errorUploads.push({
                    status: status,
                    response: JSON.parse(response),
                    item: item
                });
            };

            this.service.getUploaderService().onCompleteItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
                if (status === 200) {
                    const res = JSON.parse(response);
                  if (_.has(res, 'file') && res.file) {

                      // mark file that has been uploaded, so it won't be uploaded again
                      item.additional['uploaded'] = true;

                        const fileInfo = item.additional;
                        if (!_.has(updatedFileInfo, fileInfo.question)) {
                            updatedFileInfo[fileInfo.question] = {};
                        }
                        // once file upload completed, use the file id from res to update model[key] = file_name[timestamp] to be model[key] = file_id before saving to DB
                        if (_.has(data.pageData, fileInfo.question)) {
                            if (_.isArray(data.pageData[fileInfo.question])) {
                                const fileIndex = _.indexOf(data.pageData[fileInfo.question], `${item.file.name}[${fileInfo.timeStamp}]`);
                                if (fileIndex > -1) {
                                    data.pageData[fileInfo.question][fileIndex] = res.file;
                                    updateFn(fileInfo.question, res.file, fileIndex);
                                }
                            } else {
                                data.pageData[fileInfo.question] = res.file;
                                updateFn(fileInfo.question, res.file);
                            }

                            if (!_.has(updatedFileInfo[fileInfo.question], res.file)) {
                                updatedFileInfo[fileInfo.question][res.file] = {};
                            }
                            updatedFileInfo[fileInfo.question][res.file] = res._embedded[res.file];
                        }
                    }
                }
            };

            // action upload each FileItem from the uploader queue instead of uploadAll
            // as we only upload new FileItems that without fileid
            // existing FileItems that have fileid, do not need upload again
            // this.service.getUploaderService().uploadAll();

            var fileItems_uploaded = _.filter(
              this.service.getUploaderService().queue,
              (fItem: FileItem) => fItem.additional['uploaded'] !== undefined && fItem.additional['uploaded'] === true);

            // old files are from DB, news files are old files plus new files(not uploaded, and uploaded)
            // if new files minus old files is the same number of already uploaded files, then no need to upload files, just save the page
            if (fileItems_uploaded.length == this.service.getUploaderService().queue.length - numOfOldFiles) {
                //console.log("same length");
                // all files in the queue have been uploaded previously, just save page
                if (isFormDirtyAndValid || alwaysSaveData) {
                    //console.log("save only");
                    this.saveFormDataAndChangePage(data); // save page
                }
            }
            else
            {
              // queue has new un-uploaded files
              for (var i = 0; i < this.service.getUploaderService().queue.length; i++)
              {
                // upload FileItem individually
                var fileItem = this.service.getUploaderService().queue[i];
                if (fileItem.additional && fileItem.additional['fileid'] === undefined && fileItem.additional['uploaded'] !== undefined && fileItem.additional['uploaded'] === false)
                {
                  //console.log("upload new file, file index " + this.service.getUploaderService().getIndexOfItem(fileItem));
                  this.service.getUploaderService().uploadItem(fileItem);
                }
              }
            }

            this.service.getUploaderService().onCompleteAll = () => {
                  this.store.dispatch(new DisableBusyFlag(-1));
                  if (errorUploads.length) {
                      let errorMessage = '<ul>';
                      errorUploads.forEach(error => {
                          let body = `<li>File: ${error.item.file.name} - Error (${error.status}): ${error.response.message}</li>`;
                          errorMessage += body;
                          const fileInfo = error.item.additional;
                          const fileIndex = _.indexOf(data.pageData[fileInfo.question], `${error.item.file.name}[${fileInfo.timeStamp}]`);

                          updateFn(fileInfo.question, '');

                      });
                      errorMessage += '</ul>';
                      showingErrorFn('Upload file failure', errorMessage);
                      this.isFormDirty = true;
                  } else {
                      if (isFormDirtyAndValid || alwaysSaveData) {
                          //console.log("save only");
                          this.saveFormDataAndChangePage(data); // save page
                      } else {
                          //console.log("save and navigate");
                          if (this.saveFormDataAndChangePage(data)) {
                            this.store.dispatch(new stageActions.SelectPageAction(nextPageId)); // navi page
                        }
                      }
                  }
              };


        }
        else {
            if (isFormDirtyAndValid || alwaysSaveData) {
                //console.log("save only");
                this.saveFormDataAndChangePage(data);// save page
            } else {
                //console.log("save and navigate");
                if (this.saveFormDataAndChangePage(data)) {
                    this.store.dispatch(new stageActions.SelectPageAction(nextPageId)); // navi page
              }
            }
        }

        //return data.pageData;
        return effect;
    }

    saveFormDataAndChangePage(data: stageTypes.IFormData): void {
        this.store.dispatch(new stageActions.SaveFormDataAction(data));
    }

    // for personal detail

    getCurrentStagePageLength$(): Observable<number> {
        return this.store.select(fromRoot.getStageDataGetStagePagesLength);
    }

    getCurrentPageId$(): Observable<number> {
        return this.store.select(fromRoot.getStageDataGetSelectedPageId);
    }

    getStagePages$(): Observable<stageTypes.IPage[]> {
        return this.store.select(fromRoot.getStageDataGetStagePages);
    }

    getUploader(): FileUploader {
        return this.service.getUploaderService();
    }


    confirmDocument$(name: string, href: string) {
        let res = this.service.confirmDocument(name, href);
        return res;
    }

    // for document confirmation page
    getFileConfirmationStatus$(questionId: number): Observable<boolean>{
        return this.store.select(fromRoot.getStageDataGetReadAttachments)
            .map( (statuses: commonTypes.IStatus[]) => {
                if (!statuses || (statuses && !statuses.length)) {
                    return false;
                }
                const readAttachment: commonTypes.IStatus = _.find(statuses, (status: commonTypes.IStatus) => status.currentId === String(questionId));
                return (readAttachment && readAttachment.confirmed);
            });
    }

    getAllAttachmentsConfirmationStatus$(): Observable<boolean> {
        return this.store.select(fromRoot.getStageDataAllAttachmentsConfirmed);
    }

    onDocumentAction(actionName: string, questionId: number) {
        return this.store.select(fromRoot.getStageDataGetSelectedPageData)
            .subscribe ( (pageData: stageTypes.IPage) => {
                if (pageData) {
                    let questionData: stageTypes.IHtmlElement;
                    if (this.currentStageId === 3) {
                        questionData = _.find(pageData.htmlelements, (htmlElement: stageTypes.IHtmlElement) => htmlElement.id === questionId );
                    } else {
                        questionData = (_.has(pageData.elements, questionId)) ? pageData.elements[questionId] : null;
                    }

                    if (questionData) {
                        this.dispatchAction(actionName.toLowerCase(), questionData);
                    }
                }
            });
    }

    dispatchAction(actionName: string, questionData: stageTypes.IHtmlElement) {
        const action: stageTypes.IAction =(actionName.toLowerCase() === 'confirm') ? questionData.confirm : _.find(questionData.actions, (action: stageTypes.IAction) => action.name.toLowerCase() === actionName.toLowerCase());
        let payload: stageTypes.IAttachmentDownload = {
            questionId: questionData.id,
            name: questionData.name,
            link: action.href,
            authRequired: true,
            action: actionName.toLowerCase()
        };
        switch (actionName) {
            case 'add to calendar':
                if (payload.name === '') {
                    payload.name = 'addToCalendar.ics';
                }
            case 'download all':
            case 'view':
                this.dispatchDownload(payload);
                break;
            case 'update':
                this.store.dispatch(new stageActions.UpdateStageAction(payload));
                break;
            case 'confirm':
                this.store.dispatch(new stageActions.DocumentConfirmationAction(payload));
                break;
            default:
                this.store.dispatch(new stageActions.ServerCallAction(payload));
                break;
        }
    }

    confirmAllDocuments$(): Observable<any> {
        let res = this.service.acceptStage();
        return res;
    }

    // STAGE 4: Mobile confirmation
    getMobileStageInfo$(): Observable<stageTypes.IMobileStageResponse> {
        return this.store.select(fromRoot.getStageDataGetStageMobileInfo);
    }

    verifyCode$(code: string, secret: string): Observable<commonTypes.IResponse> {
        if (this.currentStageId == 31) {
            return this.service.verifyDeclineOfferCode(code, secret);
        }
        else {
            return this.service.verifyCode(code, secret);
        }
    }

    resendSMS$(mobNumber: string, secret: string): Observable<commonTypes.IResponse> {
        return this.service.resendSMS(mobNumber, secret);
    }

    // stage 5, 6
    groupElements(entity: stageTypes.IPage): stageTypes.IContainerDef[] {
        let ret: stageTypes.IContainerDef[] = [];
        entity.containers.map((container: stageTypes.IContainerDef) => {
            let cont: stageTypes.IContainerDef = _.cloneDeep(container);
            cont.fields = [];
            container.fields.map( (id: string) => {
                if (_.has(entity.elements, id)) {
                    cont.fields.push(entity.elements[id]);
                }
            });
            ret.push(cont);
        });

        return ret;
    }
}



// WEBPACK FOOTER //
// ./src/app/stage/services/stage.sandbox.ts